import { Inject, Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, mapTo, shareReplay, switchMap, take, tap, throwIfEmpty } from 'rxjs/operators';
import { ApBaseClass, Destroyable$ } from 'webcommon/shared/ap-base-class-ng1520';
import { ignoreErrorAndRethrow } from 'webcommon/shared/errors-ng1520';
import { LoadTracker } from './LoadTracker';

// usage: in components, where we need to share the result of a request to the server across multiple subscribers,
// and also track loading, and also allow for on-demand reloads with specific event args
@Injectable()
export class EventComposite<TLoadValue, TChangeEventArgs = null> extends ApBaseClass implements OnDestroy {
  private readonly loadTracker = LoadTracker.create(this, 'load', 'preload');
  get loading$(): Observable<boolean> {
    return this.loadTracker.safeLoadingChange$;
  }

  readonly change$ = new Subject<TChangeEventArgs>();

  private readonly _load$ = new ReplaySubject<TChangeEventArgs>(1);
  readonly shouldStartLoad$: Observable<TChangeEventArgs> = this._load$.pipe(
    switchMap((value) => {
      const loadId = this.loadTracker.start('preload');
      return this.preLoadFn$(value).pipe(
        this.loadTracker.finalizeStopLoad(loadId),
        // todo: create a separate stream for error??
        ignoreErrorAndRethrow(),
        filter(_value => _value),
        mapTo(value),
      );
    }),
    shareReplay({
      refCount: false,
      bufferSize: 1,
    }),
  );
  readonly afterLoad$: Observable<TLoadValue> = this.shouldStartLoad$.pipe(
    switchMap((value) => {
      const loadId = this.loadTracker.start('load');
      return (this.loadFn$?.(value) || EMPTY).pipe(
        this.loadTracker.finalizeStopLoad(loadId),
        ignoreErrorAndRethrow(),
      );
    }),
    shareReplay({
      refCount: false,
      bufferSize: 1,
    }),
  );

  constructor(
    @Inject('EventComposite_destroyable') private destroyable?: Destroyable$,
    @Inject('EventComposite_loadFn$') private loadFn$?: (event: TChangeEventArgs) => Observable<TLoadValue>,
    @Inject('EventComposite_preLoadFn$') private preLoadFn$: (event: TChangeEventArgs) => Observable<boolean> = () => of(true),
  ) {
    super();

    this.destroyable?.onDestroy$.subscribe(() => {
      this.ngOnDestroy();
    });

    this.change$.pipe(
      tap((value) => {
        // on change, try to reload
        this._load$.next(value);
      }),
    ).subscribe();
  }

  // this function is for help with type inference
  static create<TLoadValue, TChangeEventArgs = null>(
    ...args: ConstructorParameters<typeof EventComposite<TLoadValue, TChangeEventArgs>>
  ): EventComposite<TLoadValue, TChangeEventArgs> {
    return new EventComposite<TLoadValue, TChangeEventArgs>(...args);
  }

  getAfterLoadP(): Promise<TLoadValue> {
    if (!this.loadFn$) {
      return Promise.reject(new Error('EventComposite: trying to create Promise before setup'));
    }

    // some kind of 'take' operator is necessary here,
    // because afterLoad$ never completes until ngOnDestroy,
    // and the Promise won't resolve until the Observable completes
    return this.afterLoad$.pipe(
      throwIfEmpty(() => new Error('EventComposite: a value was never emitted from afterLoad$ before complete()')),
      take(1),
    ).toPromise();
  }

  setup(
    loadFn$: (event: TChangeEventArgs) => Observable<TLoadValue>,
    preLoadFn$: (event: TChangeEventArgs) => Observable<boolean> = () => of(true),
  ): boolean {
    if (this.loadFn$) {
      // this only allows it to be setup once
      return false;
    }

    this.loadFn$ = loadFn$;
    this.preLoadFn$ = preLoadFn$;
    return true;
  }

  setupAndStart(
    initialEvent: TChangeEventArgs,
    loadFn$: (event: TChangeEventArgs) => Observable<TLoadValue>,
    preLoadFn$: (event: TChangeEventArgs) => Observable<boolean> = () => of(true),
  ): this {
    const setupSuccess = this.setup(loadFn$, preLoadFn$);
    if (setupSuccess) {
      this.change$.next(initialEvent);
    }
    return this;
  }

  start(
    initialEvent: TChangeEventArgs,
  ): this {
    this.change$.next(initialEvent);
    return this;
  }

  override ngOnDestroy() {
    this.change$.complete();
    this._load$.complete();
    super.ngOnDestroy();
  }
}
